import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { CheckIcon, XIcon } from '@phosphor-icons/react';
import {
  UPLOAD_RULE_COLUMNS,
  type UploadLayout,
  type UploadRuleCheck,
  type UploadRuleColumn,
  type UploadRuleSeverity,
} from '@erp/rpc';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  checkFromForm,
  type RuleCheckType,
  type RuleFormError,
  type RuleFormState,
} from './ruleForm';
import { useRuleText } from './useRuleText';

const CHECK_TYPES: RuleCheckType[] = [
  'required',
  'equals',
  'oneOf',
  'numberBetween',
  'maxLength',
];
const SEVERITIES: UploadRuleSeverity[] = ['block', 'warn'];
/** Select value standing for "no supplier scope". */
const ALL_SUPPLIERS = '__all__';

function isColumn(value: unknown): value is UploadRuleColumn {
  return UPLOAD_RULE_COLUMNS.some((column) => column === value);
}
function isCheckType(value: unknown): value is RuleCheckType {
  return CHECK_TYPES.some((type) => type === value);
}
function isSeverity(value: unknown): value is UploadRuleSeverity {
  return SEVERITIES.some((severity) => severity === value);
}

interface Props {
  initial: RuleFormState;
  /** Supplier layouts a rule can be limited to. */
  layouts: UploadLayout[];
  isSaving: boolean;
  onSave: (rule: {
    column: UploadRuleColumn;
    check: UploadRuleCheck;
    severity: UploadRuleSeverity;
    layoutId: string | null;
  }) => void;
  onCancel: () => void;
}

/** One inline row for adding or editing an upload rule. */
export function RuleEditor({
  initial,
  layouts,
  isSaving,
  onSave,
  onCancel,
}: Props) {
  const gt = useGT();
  const { columnLabel, checkTypeNames, severityNames } = useRuleText();
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<RuleFormError | null>(null);

  function update(changes: Partial<RuleFormState>): void {
    setForm((current) => ({ ...current, ...changes }));
    setError(null);
  }

  function handleSave(): void {
    const result = checkFromForm(form);
    if ('error' in result) {
      setError(result.error);
      return;
    }
    onSave({
      column: form.column,
      check: result.check,
      severity: form.severity,
      layoutId: form.layoutId,
    });
  }

  const errorMessages: Record<RuleFormError, string> = {
    value: gt('Enter the value to check against.', {
      context: 'upload rule editor validation error',
    }),
    range: gt('Enter a minimum, a maximum, or both (minimum ≤ maximum).', {
      context: 'upload rule editor validation error',
    }),
    length: gt('Enter a whole number of characters.', {
      context: 'upload rule editor validation error',
    }),
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={form.column}
          onValueChange={(value) => {
            if (isColumn(value)) update({ column: value });
          }}
        >
          <SelectTrigger className="h-8 w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {UPLOAD_RULE_COLUMNS.map((column) => (
              <SelectItem key={column} value={column}>
                {columnLabel(column)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={form.checkType}
          onValueChange={(value) => {
            if (isCheckType(value)) update({ checkType: value });
          }}
        >
          <SelectTrigger className="h-8 w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CHECK_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {checkTypeNames[type]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <CheckInputs form={form} onChange={update} />

        <Select
          value={form.severity}
          onValueChange={(value) => {
            if (isSeverity(value)) update({ severity: value });
          }}
        >
          <SelectTrigger className="h-8 w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SEVERITIES.map((severity) => (
              <SelectItem key={severity} value={severity}>
                {severityNames[severity]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={form.layoutId ?? ALL_SUPPLIERS}
          onValueChange={(value) => {
            if (typeof value !== 'string') return;
            update({ layoutId: value === ALL_SUPPLIERS ? null : value });
          }}
        >
          <SelectTrigger className="h-8 w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SUPPLIERS}>
              {gt('All suppliers', {
                context: 'upload rule applies to every supplier layout',
              })}
            </SelectItem>
            {layouts.map((layout) => (
              <SelectItem key={layout.id} value={layout.id}>
                {layout.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button size="sm" onClick={handleSave} disabled={isSaving}>
          <CheckIcon className="h-4 w-4" />
          <T context="button that saves an upload validation rule">Save Rule</T>
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          disabled={isSaving}
        >
          <XIcon className="h-4 w-4" />
          <T context="button that cancels editing an upload validation rule">
            Cancel
          </T>
        </Button>
      </div>
      {error ? (
        <p className="text-destructive text-sm">{errorMessages[error]}</p>
      ) : null}
    </div>
  );
}

function CheckInputs({
  form,
  onChange,
}: {
  form: RuleFormState;
  onChange: (changes: Partial<RuleFormState>) => void;
}) {
  const gt = useGT();
  const ctx = { context: 'placeholder in the upload rule editor' };
  if (form.checkType === 'required') return null;
  if (form.checkType === 'numberBetween') {
    return (
      <>
        <Input
          className="h-8 w-24"
          inputMode="decimal"
          placeholder={gt('Min', ctx)}
          value={form.min}
          onChange={(event) => onChange({ min: event.target.value })}
        />
        <Input
          className="h-8 w-24"
          inputMode="decimal"
          placeholder={gt('Max', ctx)}
          value={form.max}
          onChange={(event) => onChange({ max: event.target.value })}
        />
      </>
    );
  }
  const placeholders: Record<'equals' | 'oneOf' | 'maxLength', string> = {
    equals: gt('Value, e.g. 261001', ctx),
    oneOf: gt('Values, comma-separated', ctx),
    maxLength: gt('Characters', ctx),
  };
  return (
    <Input
      className="h-8 w-56"
      placeholder={placeholders[form.checkType]}
      value={form.value}
      onChange={(event) => onChange({ value: event.target.value })}
    />
  );
}
